/**
 * Inbox surface types — Phase 03 / 06 / 08.
 *
 * Phase 03 created the backend `inbox_item` table + emit machinery.
 * Phase 06 introduced the typed `listInbox` client and the
 * np_missing_in_backoffice banner pattern. Phase 08 widens the SPA
 * type to mirror the full backend `InboxItemResponse` shape (snooze /
 * resolve / dismiss fields, chat_thread entity type, codename_cached)
 * so the full Inbox UI can render every column.
 *
 * `extra` stays loosely typed (`Record<string, unknown>`) — per-kind
 * payloads (e.g. Phase 06's `{missing_partners, missing_subjects,
 * missing_components}`) are accessed via narrow helpers at call sites
 * rather than expanded here, so adding a new kind never requires a
 * type-graph change.
 */

export type InboxState = 'open' | 'snoozed' | 'resolved' | 'dismissed';
export type InboxPriority = 'low' | 'normal' | 'high';
export type InboxResolutionKind = 'completed' | 'dismissed' | 'auto_resolved';
export type InboxCreatedByKind = 'system' | 'analyst';
export type InboxEntityType =
  | 'enquiry'
  | 'report_request'
  | 'report'
  | 'research_plan_item'
  | 'chat_thread';

export type InboxCourt = 'needs_you' | 'waiting';

export interface InboxItem {
  id: string;
  kind: string;
  /** Ball-in-court classification (server-derived from kind): 'waiting'
   * = the ball is with the client (activation/approval); everything
   * else — including inbound chat, which needs a reply — 'needs_you'. */
  court: InboxCourt;
  entityType: InboxEntityType | string;
  entityId: string;
  teamId: string | null;
  codenameCached: string | null;
  /** Family root codename — the rename key for this item's matter. */
  codenameFamilyCached: string | null;
  state: InboxState;
  priority: InboxPriority;
  assignedToUserId: string | null;

  wakeAt: string | null;
  snoozedByUserId: string | null;
  snoozedAt: string | null;
  snoozeReason: string | null;

  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionKind: InboxResolutionKind | null;
  resolutionNotes: string | null;

  title: string;
  body: string | null;
  extra: Record<string, unknown> | null;

  createdAt: string;
  updatedAt: string | null;
  lastSeenAt: string | null;
  createdByKind: InboxCreatedByKind;

  // ------------------------------------------------------------------
  // Queue-row enrichment (grouped endpoint only) — computed server-side
  // from the item's underlying entity. Null = not applicable.
  // ------------------------------------------------------------------
  /** Decimal string, e.g. "1500.00". */
  price: string | null;
  /** ISO 4217 from the family's report config, e.g. "GBP". */
  currency: string | null;
  turnaroundDays: number | null;
  expedited: boolean | null;
  /** The row's clock: report due (started_at + TAT) or, on
   * np_fulfilment_pending, the partner's due date. */
  dueAt: string | null;
  /** Per-item assignee (name-resolved) — the case-owner OVERRIDE for
   * handing one item to someone else. Null = no override. */
  assignee: InboxAnalyst | null;
}

/**
 * One codename-keyed group from `GET /inbox/grouped`. Items inside
 * carry their original `entityType` / `entityId` so per-row deep-links
 * still point at the specific entity; the `key` is the group's
 * display header.
 */
/**
 * Client/company that owns a codename group — resolved server-side from knox.
 * `null` means "unknown / not available" (no team, knox miss, or knox not yet
 * populated), NOT "no client exists". Only `name` is guaranteed when non-null.
 */
export interface InboxClient {
  teamId: string;
  name: string;
  clientCode: string | null;
  organizationId: number | null;
}

/**
 * At-a-glance rollup of the entity family behind a codename group,
 * computed server-side. `null` on a group means no resolvable family
 * root (the "(uncoded)" bucket).
 */
export interface InboxGroupSummary {
  rootEntityType: 'enquiry' | 'report' | 'report_request' | null;
  rootEntityId: string | null;
  enquiryStage: string | null;
  reportRequestCount: number;
  /** Triage stage value → count, e.g. { ready_to_start: 1, initial: 1 }. */
  triageStages: Record<string, number>;
  /** Report lifecycle stage value → count. */
  reportStages: Record<string, number>;
  /** Deduped and capped server-side; `subjectTotal` is the uncapped count. */
  subjectNames: string[];
  subjectTotal: number;
  expedited: boolean;
  codenameIsCustom: boolean;
  /** Case-level owner from the family root; null = unclaimed (or a
   * self-served report root, which is unclaimable). */
  analystUserId: string | null;
}

/** The case-owning analyst, name-resolved server-side (best-effort —
 * a lookup miss leaves `name` null and the SPA falls back to the id). */
export interface InboxAnalyst {
  userId: string;
  name: string | null;
}

export interface InboxGroup {
  key: string;
  client: InboxClient | null;
  analyst: InboxAnalyst | null;
  summary: InboxGroupSummary | null;
  itemCount: number;
  items: InboxItem[];
}

export interface InboxGroupedResponse {
  groups: InboxGroup[];
  /** Families across ALL pages — drives the pager. */
  groupTotal: number;
  /** Items across all pages. */
  itemTotal: number;
  page: number;
  pageSize: number;
}

export interface InboxListParams {
  state?: InboxState;
  kind?: string;
  priority?: InboxPriority;
  entityType?: InboxEntityType;
  entityId?: string;
  assignedTo?: string;
  teamId?: string;
  includeTeamNull?: boolean;
  page?: number;
  limit?: number;
}

export interface InboxGroupedParams {
  state?: InboxState;
  kind?: string;
  priority?: InboxPriority;
  assignedTo?: string;
  teamId?: string;
  includeTeamNull?: boolean;
  /** Case-insensitive substring over title/body/codenames. */
  search?: string;
  /** 1-based page over GROUPS (codename families). */
  page?: number;
  /** Groups per page (backend default 20, max 100). */
  pageSize?: number;
}

export interface InboxListResponse {
  items: InboxItem[];
  total: number;
}

// ---------------------------------------------------------------------------
// Mutation request bodies
// ---------------------------------------------------------------------------

export interface InboxSnoozeRequest {
  /** ISO-8601 UTC timestamp; must be in the future per backend rule. */
  wakeAt: string;
  reason?: string | null;
}

export interface InboxDismissRequest {
  reason: string;
}

export interface InboxAssignRequest {
  /** `null` releases the item back to the team queue. */
  userId: string | null;
}

export interface InboxResolveRequest {
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// API-shape transformers (snake_case → camelCase)
// ---------------------------------------------------------------------------

/**
 * API-shape (snake_case) of a single inbox item.
 *
 * The audit fields are optional because the grouped endpoint serves a
 * SLIM shape (no snooze/resolution audit detail, allow-listed `extra`);
 * the flat `GET /inbox` and `GET /inbox/{id}` still return everything.
 * The transform defaults the gaps so both shapes map onto one
 * `InboxItem` type.
 */
interface ApiInboxItem {
  id: string;
  kind: string;
  court?: InboxCourt;
  entity_type: string;
  entity_id: string;
  team_id: string | null;
  codename_cached: string | null;
  codename_family_cached?: string | null;
  state: InboxState;
  priority: InboxPriority;
  assigned_to_user_id: string | null;
  wake_at: string | null;
  snoozed_by_user_id?: string | null;
  snoozed_at?: string | null;
  snooze_reason?: string | null;
  resolved_at?: string | null;
  resolved_by_user_id?: string | null;
  resolution_kind?: InboxResolutionKind | null;
  resolution_notes?: string | null;
  title: string;
  body: string | null;
  extra: Record<string, unknown> | null;
  created_at: string;
  updated_at: string | null;
  last_seen_at: string | null;
  created_by_kind?: InboxCreatedByKind;
  price?: string | number | null;
  currency?: string | null;
  turnaround_days?: number | null;
  expedited?: boolean | null;
  due_at?: string | null;
  assignee?: { user_id: string; name?: string | null } | null;
}

export function transformToInboxItem(api: ApiInboxItem): InboxItem {
  return {
    id: api.id,
    kind: api.kind,
    court: api.court ?? 'needs_you',
    entityType: api.entity_type,
    entityId: api.entity_id,
    teamId: api.team_id,
    codenameCached: api.codename_cached,
    codenameFamilyCached: api.codename_family_cached ?? null,
    state: api.state,
    priority: api.priority,
    assignedToUserId: api.assigned_to_user_id,
    wakeAt: api.wake_at,
    snoozedByUserId: api.snoozed_by_user_id ?? null,
    snoozedAt: api.snoozed_at ?? null,
    snoozeReason: api.snooze_reason ?? null,
    resolvedAt: api.resolved_at ?? null,
    resolvedByUserId: api.resolved_by_user_id ?? null,
    resolutionKind: api.resolution_kind ?? null,
    resolutionNotes: api.resolution_notes ?? null,
    title: api.title,
    body: api.body,
    extra: api.extra,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    lastSeenAt: api.last_seen_at,
    createdByKind: api.created_by_kind ?? 'system',
    price: api.price != null ? String(api.price) : null,
    currency: api.currency ?? null,
    turnaroundDays: api.turnaround_days ?? null,
    expedited: api.expedited ?? null,
    dueAt: api.due_at ?? null,
    assignee: api.assignee
      ? { userId: api.assignee.user_id, name: api.assignee.name ?? null }
      : null,
  };
}

interface ApiInboxClient {
  team_id: string;
  name: string;
  client_code: string | null;
  organization_id: number | null;
}

interface ApiInboxGroupSummary {
  root_entity_type?: 'enquiry' | 'report' | 'report_request' | null;
  root_entity_id?: string | null;
  enquiry_stage?: string | null;
  report_request_count?: number;
  triage_stages?: Record<string, number>;
  report_stages?: Record<string, number>;
  subject_names?: string[];
  subject_total?: number;
  expedited?: boolean;
  codename_is_custom?: boolean;
  analyst_user_id?: string | null;
}

interface ApiInboxAnalyst {
  user_id: string;
  name?: string | null;
}

interface ApiInboxGroup {
  key: string;
  client: ApiInboxClient | null;
  analyst?: ApiInboxAnalyst | null;
  summary?: ApiInboxGroupSummary | null;
  item_count?: number;
  items: ApiInboxItem[];
}

interface ApiInboxGroupedResponse {
  groups: ApiInboxGroup[];
  group_total?: number;
  item_total?: number;
  /** Transitional alias of item_total from the pre-pagination shape. */
  total?: number;
  page?: number;
  page_size?: number;
}

function transformToInboxGroupSummary(api: ApiInboxGroupSummary): InboxGroupSummary {
  return {
    rootEntityType: api.root_entity_type ?? null,
    rootEntityId: api.root_entity_id ?? null,
    enquiryStage: api.enquiry_stage ?? null,
    reportRequestCount: api.report_request_count ?? 0,
    triageStages: api.triage_stages ?? {},
    reportStages: api.report_stages ?? {},
    subjectNames: api.subject_names ?? [],
    subjectTotal: api.subject_total ?? 0,
    expedited: api.expedited ?? false,
    codenameIsCustom: api.codename_is_custom ?? false,
    analystUserId: api.analyst_user_id ?? null,
  };
}

export function transformToInboxGrouped(api: ApiInboxGroupedResponse): InboxGroupedResponse {
  return {
    groupTotal: api.group_total ?? api.groups.length,
    itemTotal: api.item_total ?? api.total ?? 0,
    page: api.page ?? 1,
    pageSize: api.page_size ?? api.groups.length,
    groups: api.groups.map((g) => ({
      key: g.key,
      client: g.client
        ? {
            teamId: g.client.team_id,
            name: g.client.name,
            clientCode: g.client.client_code ?? null,
            organizationId: g.client.organization_id ?? null,
          }
        : null,
      analyst: g.analyst ? { userId: g.analyst.user_id, name: g.analyst.name ?? null } : null,
      summary: g.summary ? transformToInboxGroupSummary(g.summary) : null,
      itemCount: g.item_count ?? g.items.length,
      items: g.items.map(transformToInboxItem),
    })),
  };
}

// ---------------------------------------------------------------------------
// Browse — GET /inbox/browse (the admin navigation surface)
// ---------------------------------------------------------------------------

export type InboxBrowseKind = 'enquiry' | 'report_request' | 'report';
export type InboxBrowseMode = 'cases' | 'all';

export interface ApiInboxBrowseRow {
  kind: InboxBrowseKind;
  id: string;
  codename: string | null;
  codename_family: string | null;
  stage: string;
  team_id: string | null;
  created_at: string | null;
}

export interface ApiInboxBrowseResponse {
  items: ApiInboxBrowseRow[];
  /** `all`: rows across all pages; `cases`: codename families. */
  total: number;
  page: number;
  page_size: number;
  mode: InboxBrowseMode;
  version: string | null;
}

export interface InboxBrowseParams {
  q?: string;
  teamId?: string;
  kinds?: InboxBrowseKind[];
  mode: InboxBrowseMode;
  page: number;
  pageSize: number;
}
